<script>
import {defineComponent, onBeforeMount, reactive, ref, watch, computed} from "vue";
import InputText from "primevue/inputtext";
import Editor from "primevue/editor";
import MainCard from "@/components/cards/MainCard";
import ImageCard from "@/components/cards/ImageCard.vue";
import ButtonFileUpload from "@/components/buttons/ButtonFileUpload.vue";
import ButtonSuccess from "@/components/buttons/ButtonSuccess.vue";
import Button from "primevue/button";
import {useApiCall} from "@/composables/useApiCall";
import {
  branApartApiCall,
  updateBranApartApiCall,
  uploadBranApartPhotoApiCall,
  deleteBranApartPhotoApiCall,
} from "@/services/website";
import Skeleton from "primevue/skeleton"
import {useI18n} from "vue-i18n";
import {useVuelidate} from "@vuelidate/core";
import {required} from "@/utils/i18n-validators";
import {useStore} from "vuex";

export default defineComponent({
  components: {
    ButtonSuccess,
    ButtonFileUpload,
    ImageCard,
    InputText,
    Editor,
    MainCard,
    Button,
    Skeleton,
  },
  setup() {
    const { t } = useI18n()
    const store = useStore();
    const form = reactive({
      fields: {
        title_ru: '',
        description_ru: '',
        description_en: '',
        photos: []
      }
    });
    const files = ref([])
    const lang = computed(() => store.getters.getLang);
    const descriptionField = computed(() => `description_${lang.value}`);
    const isUpdated = ref(false)
    const deleteUuid = ref([])
    const sendIndex = ref(null)
    const formRules = computed(() => ({
      [descriptionField.value]: {required},
      photos: {}
    }))
    const v$ = useVuelidate(formRules, form.fields);
    const {
      isLoading: isContentLoading,
      data: contentData,
      error: contentError,
      executeApiCall: getContentAction,
    } = useApiCall(branApartApiCall, true)

    const {
      isLoading: isContentUpdateLoading,
      data: contentUpdateData,
      error: contentUpdateError,
      executeApiCall: updateContentAction,
    } = useApiCall(updateBranApartApiCall, true)

    const {
      isLoading: isContentDeletePhotoLoading,
      executeApiCall: deleteContentPhotoAction,
    } = useApiCall(deleteBranApartPhotoApiCall, true)

    const {
      isLoading: isContentPhotoLoading,
      error: contentPhotoError,
      executeApiCall: uploadContentPhotoAction,
    } = useApiCall(uploadBranApartPhotoApiCall, true)

    const destroyFileMemory = (file) => {
      files.value = files.value.filter(item => item !== file)
    }
    const loadAboutFileMemory = (data) => {
      files.value = files.value.concat(data.files)
    }

    const deletePhoto = (uuid) => {
      form.fields.photos = form.fields.photos.filter(item => item.uuid !== uuid)
      deleteUuid.value.push(uuid)
    }
    const updateForm = async () => {
      const result = await v$.value.$validate();
      if (!result) {
        return;
      }
      sendIndex.value = null
      try {
        if (deleteUuid.value.length) {
          for (let i = 0; i < deleteUuid.value.length; i += 1) {
            await deleteContentPhotoAction({uuid: deleteUuid.value[i]})
          }
        }
        if (files.value.length) {
          for (let i = 0; i < files.value.length; i += 1) {
            sendIndex.value = files.value[i].objectURL
            const formData = new FormData()
            formData.append('type', 'improvement')
            formData.append('photo', files.value[i])
            await uploadContentPhotoAction(formData)
          }
        }
        await updateContentAction({
          title_ru: form.fields.title_ru,
          text_ru: form.fields.description_ru,
          text_en: form.fields.description_en
        })
        if (contentUpdateData.value) {
          await deleteLocalData()
          isUpdated.value = true
        }
      } catch (e) {
        console.error(e)
      }
    }

    const deleteLocalData = async () => {
      form.fields.photos = contentUpdateData.value.data.photos
      files.value = []
      deleteUuid.value = []
    }

    watch(() => [form.fields, files.value, deleteUuid.value],
        ()=> {
          isUpdated.value = false
        }, {deep: true})

    onBeforeMount( async () => {
      try {
        await getContentAction()
        if (contentData.value?.data) {
          form.fields.title_ru = contentData.value.data.title_ru
          form.fields.description_ru = contentData.value.data.text_ru
          form.fields.description_en = contentData.value.data.text_en ?? ''
          form.fields.photos = contentData.value.data.photos
        }
      } catch (e) {
        console.error(e)
      }
    })

    return {
      t,
      v$,
      form,
      files,
      sendIndex,
      isUpdated,
      isContentLoading,
      contentError,
      isContentUpdateLoading,
      contentUpdateError,
      isContentPhotoLoading,
      isContentDeletePhotoLoading,
      contentPhotoError,
      deletePhoto,
      destroyFileMemory,
      loadAboutFileMemory,
      updateForm,
      descriptionField,
    };
  }
})
</script>

<template>
  <div class="mt-7">
    <div class="flex justify-content-between align-items-center mb-4">
      <h1>{{ t('website.brandApart.title') }}</h1>
      <ButtonSuccess v-if="isUpdated" :label="t('button.changesSaved')" class="btn-fix-height"/>
      <Button
          v-else
          @click="updateForm"
          :loading="isContentUpdateLoading || isContentPhotoLoading || isContentDeletePhotoLoading"
          :label="t('button.saveChange')"
          class="btn-primary font-light btn-fix-height"
      />
    </div>
    <Skeleton v-if="isContentLoading" width="100%" height="153px" class="mb-3"></Skeleton>
    <MainCard v-else :title="t('description')" class="mb-3">
      <Editor v-model="form.fields[descriptionField]" class="w-full"></Editor>
      <span v-if="v$[descriptionField].$errors?.length || contentUpdateError?.data[descriptionField]" class="text-xs color-error">
        {{ contentUpdateError ? contentUpdateError.data[descriptionField][0] : t(v$[descriptionField].$errors?.[0].$message) }}
      </span>
    </MainCard>
    <Skeleton v-if="isContentLoading" width="100%" height="155px"></Skeleton>
    <MainCard v-else :title="t('images.title')" >
      <div class="grid">
        <template v-if="form.fields.photos.length">
          <div v-for="(item, i) in form.fields.photos" :key="i" class="col-12 md:col-2">
            <ImageCard :src="item.preview_url" :handle="() => deletePhoto(item.uuid)"/>
          </div>
        </template>
        <template v-if="files.length">
          <div v-for="(file, i) in files" :key="i" class="col-12 md:col-2">
            <ImageCard :src="file.objectURL" :handle="() => destroyFileMemory(file)"/>
            <span v-if="contentPhotoError?.data.photo && sendIndex === file.objectURL" class="flex mb-2 text-xs color-error">
              {{ contentPhotoError.data.photo[0] }}
            </span>
          </div>
        </template>
        <div class="col-12">
          <ButtonFileUpload
              @chooseFiles="loadAboutFileMemory"
              :label="t('button.addImage')"
              :multiple="true"
              :clear-files-after-select="true"
          />
        </div>
      </div>
    </MainCard>
  </div>
</template>
